import React, { useState, useEffect } from 'react';
import { Card, Table, Tag, Typography, Row, Col, Statistic, Spin, Input, Progress, Button } from 'antd';
import { UserOutlined, SoundOutlined, CheckCircleOutlined, SearchOutlined, PrinterOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../utils/api';

const { Title, Text } = Typography;

export default function ArtistDashboard() {
  const [artists, setArtists] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const res = await api.get('/performance/artist-report');
        setArtists(Array.isArray(res.data) ? res.data : []);
      } catch (err) { console.error(err); }
      setLoading(false);
    };
    fetchData();
  }, []);

  const filtered = artists.filter(a =>
    !search || (a.artist || '').toLowerCase().includes(search.toLowerCase())
  );

  const totalSongs = artists.reduce((s, a) => s + (parseInt(a.total_songs) || 0), 0);
  const totalDist = artists.reduce((s, a) => s + (parseInt(a.distributed) || 0), 0);
  const totalPending = artists.reduce((s, a) => s + (parseInt(a.pending) || 0), 0);

  const columns = [
    { title: '#', width: 50, render: (_, __, i) => i + 1 },
    {
      title: 'Artist/Singer', dataIndex: 'artist', width: 200, sorter: (a, b) => (a.artist || '').localeCompare(b.artist || ''),
      render: v => <Text strong><UserOutlined style={{ marginRight: 4 }} />{v}</Text>
    },
    {
      title: 'Total Songs', dataIndex: 'total_songs', width: 110, sorter: (a, b) => a.total_songs - b.total_songs,
      defaultSortOrder: 'descend',
      render: v => <Tag color="blue" style={{ fontSize: 14 }}>{v}</Tag>
    },
    {
      title: 'Distributed', dataIndex: 'distributed', width: 110,
      render: v => <Tag color="green">{v}</Tag>
    },
    {
      title: 'Pending', dataIndex: 'pending', width: 100,
      render: v => parseInt(v) > 0 ? <Tag color="orange">{v}</Tag> : <Tag color="green">0</Tag>
    },
    {
      title: 'Progress', width: 130,
      render: (_, r) => {
        const pct = r.total_songs > 0 ? Math.round((r.distributed / r.total_songs) * 100) : 0;
        return <Progress percent={pct} size="small" status={pct >= 100 ? 'success' : 'active'} />;
      }
    },
    {
      title: 'IPRS', dataIndex: 'iprs_claimed', width: 80,
      render: v => <Tag color="gold">{v || 0}</Tag>
    },
    {
      title: 'Studios', dataIndex: 'studios', ellipsis: true,
      render: v => v ? v.split(',').slice(0, 3).map((s, i) => <Tag key={i} style={{ marginBottom: 2, fontSize: 11 }}>{s.trim()}</Tag>) : '-'
    }
  ];

  const handlePrint = () => {
    const w = window.open('', '_blank');
    w.document.write(`<!DOCTYPE html><html><head><title>Artist Dashboard Report</title>
      <style>body{font-family:Arial;padding:30px}.header{background:linear-gradient(135deg,#667eea,#764ba2);color:#fff;padding:20px 30px;border-radius:12px;margin-bottom:20px}
      .header h1{margin:0;font-size:22px}table{width:100%;border-collapse:collapse;margin:16px 0}td,th{border:1px solid #e8e8e8;padding:8px 12px;text-align:left;font-size:13px}th{background:#f0f5ff}
      .stats{display:flex;gap:24px;margin-bottom:16px}.stat{text-align:center}.stat b{font-size:24px;display:block}
      .footer{text-align:center;color:#999;font-size:12px;margin-top:30px;border-top:1px solid #eee;padding-top:10px}</style></head><body>
      <div class="header"><h1>Artist / Singer Dashboard</h1><p>Bainsla Music Private Limited | ${dayjs().format('DD MMM YYYY')}</p></div>
      <div class="stats"><div class="stat"><b style="color:#667eea">${artists.length}</b>Total Artists</div>
      <div class="stat"><b style="color:#1890ff">${totalSongs}</b>Total Songs</div>
      <div class="stat"><b style="color:#52c41a">${totalDist}</b>Distributed</div>
      <div class="stat"><b style="color:#faad14">${totalPending}</b>Pending</div></div>
      <table><tr><th>#</th><th>Artist</th><th>Songs</th><th>Distributed</th><th>Pending</th><th>Progress</th><th>IPRS</th><th>Studios</th></tr>
      ${filtered.map((a, i) => `<tr><td>${i+1}</td><td>${a.artist}</td><td>${a.total_songs}</td><td>${a.distributed}</td><td>${a.pending}</td>
      <td>${a.total_songs > 0 ? Math.round((a.distributed / a.total_songs) * 100) : 0}%</td><td>${a.iprs_claimed || 0}</td><td>${a.studios || '-'}</td></tr>`).join('')}
      </table>
      <div class="footer">Bainsla Music Data Dashboard | Generated on ${dayjs().format('DD MMM YYYY, hh:mm A')}</div></body></html>`);
    w.document.close();
    setTimeout(() => w.print(), 500);
  };

  return (
    <div>
      <Row justify="space-between" align="middle">
        <Col><Title level={3}><UserOutlined /> Artist / Singer Dashboard</Title></Col>
        <Col><Button type="primary" icon={<PrinterOutlined />} onClick={handlePrint}>Download PDF</Button></Col>
      </Row>
      <Text type="secondary">Artist-wise song summary and distribution status</Text>

      <Row gutter={16} style={{ margin: '16px 0' }}>
        <Col xs={12} sm={6}><Card size="small"><Statistic title="Total Artists" value={artists.length} prefix={<UserOutlined />} valueStyle={{ color: '#667eea' }} /></Card></Col>
        <Col xs={12} sm={6}><Card size="small"><Statistic title="Total Songs" value={totalSongs} prefix={<SoundOutlined />} valueStyle={{ color: '#1890ff' }} /></Card></Col>
        <Col xs={12} sm={6}><Card size="small"><Statistic title="Distributed" value={totalDist} prefix={<CheckCircleOutlined />} valueStyle={{ color: '#52c41a' }} /></Card></Col>
        <Col xs={12} sm={6}><Card size="small"><Statistic title="Pending" value={totalPending} valueStyle={{ color: '#faad14' }} /></Card></Col>
      </Row>

      <Input placeholder="Search artist..." prefix={<SearchOutlined />} value={search} onChange={e => setSearch(e.target.value)} style={{ marginBottom: 16, maxWidth: 300 }} />

      {loading ? <Spin size="large" style={{ display: 'block', textAlign: 'center', padding: 40 }} /> : (
        <Table columns={columns} dataSource={filtered} rowKey="artist" size="small" pagination={{ pageSize: 20 }} scroll={{ x: 900 }} />
      )}
    </div>
  );
}
